import { useCallback, useMemo, useRef, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Upload } from 'lucide-react';

import { ActionButton } from '@/shared/ui/action-button';
import { cn } from '@/lib/utils';

import { useFileDrop } from '../model/use-file-drop';
import { isCameraCaptureSupported, isNativeCameraPreferred } from '../model/use-camera-capture';
import { extensionsToAccept, validateUploadFile } from '../model/validate-file';
import { CameraCapture } from './camera-capture';

interface FileUploadProps {
  /** HTML `accept` hint for the picker. Defaults to a value derived from `allowedExtensions`. */
  accept?: string;
  /** Enforced allowlist of extensions, e.g. `['.xlsx']`. Undefined / empty = accept any type. */
  allowedExtensions?: string[];
  /** Enforced maximum file size in bytes. Undefined = no limit. */
  maxSize?: number;
  multiple?: boolean;
  onFilesSelected?: (files: FileList | File[]) => void;
  /**
   * Offers a *Take Photo* button next to the drop zone. Only for uploads that
   * accept images — a camera on an .xlsx picker makes no sense. On a phone or
   * tablet it opens the device's camera app; elsewhere it previews the camera in
   * place, and it hides itself where neither is available.
   */
  allowCapture?: boolean;
  /** Called with a message when a selected / dropped file fails validation. */
  onError?: (error: string) => void;
  className?: string;
  disabled?: boolean;
}

export function FileUpload({
  accept,
  allowedExtensions,
  maxSize,
  multiple = true,
  onFilesSelected,
  onError,
  allowCapture = false,
  className,
  disabled = false
}: FileUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const captureInputRef = useRef<HTMLInputElement | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  const acceptAttr = useMemo(() => accept ?? extensionsToAccept(allowedExtensions), [accept, allowedExtensions]);

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const selected = Array.from(files ?? []);
      if (selected.length === 0) return;

      for (const file of selected) {
        const result = validateUploadFile(file, { allowedExtensions, maxSize });
        if (!result.valid) {
          // Reject the whole selection if any file fails validation.
          onError?.(result.error ?? 'Invalid file');
          return;
        }
      }

      onFilesSelected?.(files);
    },
    [allowedExtensions, maxSize, onFilesSelected, onError]
  );

  const handleCapture = useCallback(
    (photo: File) => {
      setIsCapturing(false);
      handleFiles([photo]);
    },
    [handleFiles]
  );

  const { isDragging, handleDragOver, handleDragLeave, handleDrop } = useFileDrop({
    onFilesSelected: handleFiles,
    disabled
  });

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles]
  );

  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  // Read once per mount: neither the API's presence nor the pointer kind changes
  // while the page is open.
  const nativeCamera = useMemo(() => isNativeCameraPreferred(), []);
  const cameraSupported = useMemo(() => isCameraCaptureSupported(), []);

  const uploadClasses = cn(
    'flex flex-col items-center justify-center gap-4 px-6 py-8',
    'bg-muted border-2 border-dashed border-neutral-alpha-300 rounded-lg',
    'cursor-pointer transition-all duration-200 select-none',
    'hover:bg-neutral-alpha-100 hover:border-primary-600',
    'focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-2',
    {
      'bg-primary-50 border-primary-500': isDragging,
      'opacity-50 cursor-not-allowed': disabled
    },
    className
  );

  const dropZone = (
    <div
      className={uploadClasses}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={acceptAttr}
        multiple={multiple}
        onChange={handleFileChange}
        disabled={disabled}
        aria-hidden="true"
      />

      <Upload className="text-muted-foreground" size={40} />

      <div className="flex flex-col items-center gap-2">
        <span className="text-sm text-muted-foreground text-center">{t('gen.drage.file.label')}</span>
      </div>
    </div>
  );

  // Without a camera the drop zone is the whole component, exactly as before.
  if (!allowCapture || !(nativeCamera || cameraSupported)) return dropZone;

  return (
    <div className="flex flex-col gap-2">
      {isCapturing ? (
        <CameraCapture onCapture={handleCapture} onClose={() => setIsCapturing(false)} />
      ) : (
        <>
          {dropZone}

          {/* Sits outside the drop zone on purpose: `click()` bubbles, and from
              inside it would also open the plain file picker. */}
          {nativeCamera && (
            <input
              ref={captureInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              disabled={disabled}
              aria-hidden="true"
            />
          )}

          <ActionButton
            type="button"
            intent="document"
            disabled={disabled}
            onClick={() => (nativeCamera ? captureInputRef.current?.click() : setIsCapturing(true))}
            className="w-full sm:w-auto sm:self-start">
            <Camera className="mr-2 h-4 w-4" />
            {t('gen.take.photo.label', 'Take Photo')}
          </ActionButton>
        </>
      )}
    </div>
  );
}
